import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AutoBucketHistory, BucketStrategy, HistoryStore, Milliseconds, ObjectHistory, ObservationHistory, TimeBucketUnits, getNiceDate, getZombieInstanceFromPrimaryKey, } from '@iotv/datamodel-core';
import { deviceEnabledTypes } from '@iotv/payload-definition';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import AvTimerIcon from '@mui/icons-material/AvTimer';
import HistoryIcon from '@mui/icons-material/History';
import SsidChartIcon from '@mui/icons-material/SsidChart';
import TableViewIcon from '@mui/icons-material/TableView';
import { Autocomplete, Box, Checkbox, CircularProgress, Grid, IconButton, Stack, TextField, ThemeProvider, ToggleButton, Tooltip, } from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { v4 } from 'uuid';
import { AppMainViewContext } from '../../ContextProviders/AppContextProvider';
import { ActionIcons } from '../../Iconography';
import { smallerInputTheme } from '../../cosmetics/Themes/MuiInputSmaller';
import AppDao from '../../data/AppDao';
import { getObjectHistory } from '../../data/daoFunctions/daoFunctions';
import { decimalMinutesToTimeString, formatBucketedTimestamp, makePresentable, msToTime, timestampToGoogleSheetsDate } from '../../helpers/format';
import { PeriodRecent, useModularTimePeriodSelector } from '../../hooks/useModularTimePeriodSelector';
import { useSingleEntity } from '../../hooks/useSingleEntity';
import { useTimeBucketSelector } from '../../hooks/useTimeBucketSelector';
import { Severity } from '../../types';
import { StripedDataGrid } from '../factories/DataGrid';
import { SimpleIOModal } from '../io/VOBbased/SimpleIOModal';
import { IOType } from '../io/types';
import { ComparisonChart } from './ComparisonChart';
import { getBucketStrategyForTypes } from './fns/bucketStrategy';
const debug = process.env.REACT_APP_DEBUG && false;
export const ComparisonView = ({ contextCustomer }) => {
    const [{ UserFunctions }] = useContext(AppMainViewContext);
    const displayMessage = (message, type, severity) => UserFunctions.setUserMessage({ id: v4(), content: message, label: message, type, severity });
    /** DATA STORE REFS */
    // Supposedly better than keeping it all in state, though necessitates a force update
    const readingHistories = useRef({});
    const bucketedReadings = useRef({});
    const observationHistories = useRef({});
    const bucketedObservations = useRef({});
    const [forceDataUpdate, setForceDataUpdate] = useState(false);
    /** SELECTION */
    const [validTypeIds] = useState(Object.keys(deviceEnabledTypes));
    const bucketStrategyMaps = getBucketStrategyForTypes(validTypeIds);
    const [comparisonEntries, setComparisonEntries] = useState([]);
    const { components: { entityTypeSelector, entitySelector }, state: { currentEntity: [currentEntity], }, } = useSingleEntity({ contextObject: contextCustomer, validTypes: validTypeIds, currentEntityType: validTypeIds[0] });
    const addComparisonEntry = () => {
        if (!currentEntity)
            return;
        const newCompEntries = [
            ...comparisonEntries,
            {
                label: currentEntity.name ?? currentEntity.sk,
                entity: currentEntity,
                toDateMs: 'present',
                allowedKeys: [],
                keys: [],
                averageKeys: [],
                id: v4(),
            },
        ];
        setComparisonEntries(newCompEntries);
    };
    const removeComparisonEntry = (entry) => {
        delete bucketedObservations.current[entry.id];
        delete observationHistories.current[entry.id];
        delete bucketedReadings.current[entry.id];
        delete readingHistories.current[entry.id];
        setComparisonEntries((oldEntries) => {
            oldEntries.splice(oldEntries.indexOf(entry), 1);
            return [...oldEntries];
        });
        setForceDataUpdate((u) => !u);
    };
    const [fetchingReadings, setFetchingReadings] = useState({});
    const [fetchingObservations, setFetchingObservations] = useState({});
    /** TIME SELECTION */
    const { components: timeSelectorComponents, fromDateValue, toDateValue, durationMode: timeSelectorDurationMode, } = useModularTimePeriodSelector({ behaviourFlags: { preventPeriodShiftIntoFuture: true, dropSeconds: true } });
    const { state: [timeBucketDef, setTimeBucketDef], components: bucketComponents, } = useTimeBucketSelector({ unit: TimeBucketUnits.QUARTERHOUR, width: 1 });
    const bucketWidthMs = useMemo(() => timeBucketDef.unit * timeBucketDef.width, [timeBucketDef.unit, timeBucketDef.width]);
    const [averagingWindow, setAveragingWindow] = useState(3);
    // Time Changes
    useEffect(() => {
        console.log('Time Change, updating entry readings', comparisonEntries);
        const updateEntryOnTimeChange = async (entry) => {
            if (bucketedReadings.current[entry.id]) {
                setFetchingReadings((f) => ({ ...f, [entry.id]: true }));
                await fetchAndUpdateReadings(entry);
                setFetchingReadings((f) => ({ ...f, [entry.id]: false }));
            }
            if (bucketedObservations.current[entry.id])
                updateObservations(entry);
            updateAllowedKeys(entry);
            return `Updated ${entry.id}`;
            //resolve(`Updated ${entry.id}`)
        };
        const updateEntryPromises = comparisonEntries.filter((entry) => entry.toDateMs == 'present').map((entry) => updateEntryOnTimeChange(entry));
        Promise.all(updateEntryPromises).then(() => {
            console.log('Finished Updating');
            setForceDataUpdate((u) => !u);
        });
    }, [toDateValue, fromDateValue]);
    // Bucket Width Changes
    useEffect(() => {
        comparisonEntries.forEach((entry) => {
            if (bucketedReadings.current[entry.id])
                updateReadings(entry);
            if (bucketedObservations.current[entry.id])
                updateObservations(entry);
        });
        setForceDataUpdate((u) => !u);
    }, [timeBucketDef.unit, timeBucketDef.width]);
    /** READINGS */
    const fetchAndUpdateReadings = async (entry) => {
        const entity = entry.entity;
        const fromDateMs = entry.toDateMs == 'present' ? fromDateValue.valueOf() : entry.toDateMs - (toDateValue.valueOf() - fromDateValue.valueOf());
        const toDateMs = entry.toDateMs == 'present' ? toDateValue.valueOf() : entry.toDateMs;
        console.log('Fetching for', getNiceDate(fromDateMs), ' to ', getNiceDate(toDateMs));
        const oh_res = await getObjectHistory({ vob: entity, objectHistoryFilter: { recentStateHistory: { period: { fromDateMs, toDateMs } } } });
        if (!oh_res.data?.recentStateHistory) {
            displayMessage(`${entity.name} has no readings`, 'MessageBar', Severity.error);
            return;
            //throw Error("No object history")
        }
        const obHist = new ObjectHistory({ ...entity, recentStateHistory: oh_res.data?.recentStateHistory });
        if ((obHist?.recentStateHistory?.getLastTimestamp() ?? 0) < fromDateMs) {
            displayMessage(`No readings found in time period for ${entity.name}`, 'MessageBar', Severity.info);
            bucketedReadings.current[entry.id] = new HistoryStore();
            return;
        }
        readingHistories.current[entry.id] = obHist.recentStateHistory;
        updateReadings(entry);
    };
    const updateReadings = (entry) => {
        if (!readingHistories.current[entry.id])
            throw Error("Attempting to update readings that haven't been loaded yet");
        const theseReadingsBucketed = new AutoBucketHistory({
            timeBucketDef: timeBucketDef,
            defaultBucketStrategy: BucketStrategy.APPORTION,
            bucketStrategyMap: bucketStrategyMaps[entry.entity.type],
        });
        const recentStates = readingHistories.current[entry.id].getStateSnapshotArray();
        theseReadingsBucketed.ingestKeywise(recentStates);
        const fromDateMs = entry.toDateMs == 'present' ? fromDateValue.valueOf() : entry.toDateMs - (toDateValue.valueOf() - fromDateValue.valueOf());
        const toDateMs = entry.toDateMs == 'present' ? toDateValue.valueOf() : entry.toDateMs;
        bucketedReadings.current[entry.id] = theseReadingsBucketed.apportionedSliceByTimestamp([fromDateMs, toDateMs]);
    };
    const toggleReadings = (entry) => {
        if (bucketedReadings.current[entry.id]) {
            delete bucketedReadings.current[entry.id];
            updateAllowedKeys(entry);
            setForceDataUpdate((u) => !u);
        }
        else {
            setFetchingReadings((f) => ({ ...f, [entry.id]: true }));
            fetchAndUpdateReadings(entry).then(() => {
                updateAllowedKeys(entry);
                setFetchingReadings((f) => ({ ...f, [entry.id]: false }));
            });
        }
    };
    /** OBSERVATIONS */
    const fetchObservations = async (entry) => {
        let observationHistory = undefined;
        const observationHistoryZombie = getZombieInstanceFromPrimaryKey(`ObservationHistory_${entry.entity.sk}`, { pk: entry.entity?.sk });
        const res = await AppDao.getExact(observationHistoryZombie);
        if (res.data && res.data.Items instanceof Array && res.data.Items.length > 0) {
            const persistedObservationHistoryZombie = res.data.Items[0];
            if (persistedObservationHistoryZombie.observedHistory) {
                observationHistory = new ObservationHistory(persistedObservationHistoryZombie);
            }
        }
        if (observationHistory) {
            observationHistories.current[entry.id] = observationHistory.observedHistory;
            debug && console.log('Observation History', observationHistory);
        }
        else {
            throw Error(`No Observation history for ${entry.entity.name}`);
        }
    };
    const updateObservations = (entry) => {
        if (!observationHistories.current[entry.id])
            throw Error("Trying to update observations that haven't been loaded yet");
        const newBucketedObservations = new AutoBucketHistory({
            timeBucketDef,
            defaultBucketStrategy: BucketStrategy.APPORTION,
            bucketStrategyMap: bucketStrategyMaps[entry.entity.type],
        });
        newBucketedObservations.ingestKeywise(observationHistories.current[entry.id].getStateSnapshotArray());
        const fromDateMs = entry.toDateMs == 'present' ? fromDateValue.valueOf() : entry.toDateMs - (toDateValue.valueOf() - fromDateValue.valueOf());
        const toDateMs = entry.toDateMs == 'present' ? toDateValue.valueOf() : entry.toDateMs;
        const b = newBucketedObservations.apportionedSliceByTimestamp([fromDateMs, toDateMs]);
        console.log('New Observations', b);
        if (b.getLength() == 0 && !bucketedObservations.current[entry.id])
            displayMessage(`No observations found in time period for ${entry.entity.name}`, 'MessageBar', Severity.info);
        bucketedObservations.current[entry.id] = b;
    };
    const toggleObservations = (entry) => {
        if (!observationHistories.current[entry.id]) {
            // Fetch
            setFetchingObservations((f) => ({ ...f, [entry.id]: true }));
            fetchObservations(entry)
                .then(() => {
                updateObservations(entry);
                updateAllowedKeys(entry);
                setFetchingObservations((f) => ({ ...f, [entry.id]: false }));
            })
                .catch((error) => {
                console.log(error);
                displayMessage(error.message, 'MessageBar', Severity.info);
                setFetchingObservations((f) => ({ ...f, [entry.id]: false }));
            });
        }
        else if (!bucketedObservations.current[entry.id]) {
            // Observations are loaded but not active
            updateObservations(entry);
            updateAllowedKeys(entry);
            setForceDataUpdate((u) => !u);
        }
        else {
            delete bucketedObservations.current[entry.id];
            updateAllowedKeys(entry);
            setForceDataUpdate((u) => !u);
        }
    };
    /** KEY SELECTION */
    const updateAllowedKeys = (entry) => {
        const readingKeys = bucketedReadings.current[entry.id]?.keys() ?? [];
        const observationKeys = bucketedObservations.current[entry.id]?.keys() ?? [];
        const combinedKeys = [...readingKeys, ...observationKeys.filter((ok) => !readingKeys.find((rk) => rk == ok))];
        setComparisonEntries((oldEntries) => {
            let oldEntry = oldEntries.find((e) => e.id == entry.id);
            oldEntry.allowedKeys = combinedKeys;
            oldEntry.keys = oldEntry.keys.filter((k) => combinedKeys.find((ck) => ck == k));
            oldEntry.averageKeys = oldEntry.averageKeys.filter((k) => combinedKeys.find((ck) => ck == k));
            return oldEntries;
        });
    };
    const updateKeys = (reason, detail, entry) => {
        if (!detail)
            return;
        if (reason == 'selectOption') {
            setComparisonEntries((prevEntries) => {
                prevEntries.find((e) => e.id == entry.id)?.keys.push(detail.option);
                return prevEntries;
            });
        }
        else if (reason == 'removeOption') {
            setComparisonEntries((prevEntries) => {
                const entryKeys = prevEntries.find((e) => e.id == entry.id).keys;
                entryKeys.splice(entryKeys.indexOf(detail.option), 1);
                //// If key is in average, also remove
                //const entryAvgKeys = prevEntries.find(e => e.id == entry.id)!.averageKeys
                //if (entryAvgKeys.indexOf(detail.option) != -1) entryAvgKeys.splice(entryAvgKeys.indexOf(detail.option), 1)
                return [...prevEntries];
            });
        }
        setForceDataUpdate((u) => !u);
    };
    const updateAverageKeys = (reason, detail, entry) => {
        if (!detail)
            return;
        if (reason == 'selectOption') {
            setComparisonEntries((prevEntries) => {
                prevEntries.find((e) => e.id == entry.id).averageKeys.push(detail.option);
                return prevEntries;
            });
        }
        else if (reason == 'removeOption') {
            setComparisonEntries((prevEntries) => {
                const entryAvgKeys = prevEntries.find((e) => e.id == entry.id).averageKeys;
                entryAvgKeys.splice(entryAvgKeys.indexOf(detail.option), 1);
                return [...prevEntries];
            });
        }
        setForceDataUpdate((u) => !u);
    };
    /** READINGS AT DIFFERENT TIME */
    const enableDifferentTime = (entry) => {
        setComparisonEntries((prevEntries) => {
            const oldEntry = prevEntries.find((e) => e.id == entry.id);
            oldEntry.toDateMs = toDateValue.valueOf();
            return [...prevEntries];
        });
    };
    const disableDifferentTime = (entry) => {
        setComparisonEntries((prevEntries) => {
            const oldEntry = prevEntries.find((e) => e.id == entry.id);
            oldEntry.toDateMs = 'present';
            return [...prevEntries];
        });
    };
    const changeEntryDifferentTime = (newDateMs, entry) => {
        setComparisonEntries((prevEntries) => {
            const oldEntry = prevEntries.find((e) => e.id == entry.id);
            oldEntry.toDateMs = newDateMs; //- newDateMs % Milliseconds.MINUTE
            return [...prevEntries];
        });
        if (bucketedReadings.current[entry.id]) {
            setFetchingReadings((f) => ({ ...f, [entry.id]: true }));
            fetchAndUpdateReadings(entry).then(() => {
                updateAllowedKeys(entry);
                setForceDataUpdate((u) => !u);
                setFetchingReadings((f) => ({ ...f, [entry.id]: false }));
            });
        }
        if (bucketedObservations.current[entry.id]) {
            updateObservations(entry);
            updateAllowedKeys(entry);
            setForceDataUpdate((u) => !u);
        }
    };
    /** TABLE / CHART DATA */
    const [timeDisplay, setTimeDisplay] = useState('concreteBuckets');
    const formatChartTimestamp = (timestamp) => {
        if (timeDisplay == 'concreteBuckets')
            return `${formatBucketedTimestamp(timestamp, timeBucketDef.unit * timeBucketDef.width, fromDateValue.valueOf(), toDateValue.valueOf())}`;
        if (timeDisplay == 'bucketWidthsSinceStart') {
            const wholeWidths = Math.ceil((timestamp - fromDateValue.valueOf()) / bucketWidthMs);
            console.log(wholeWidths);
            return `+${msToTime(bucketWidthMs * Math.abs(wholeWidths + 1))}`;
        }
    };
    const tableData = useMemo(() => {
        debug && console.log('Bucketed Readings', bucketedReadings.current);
        debug && console.log('Bucketed Observations', bucketedObservations.current);
        const data = {
            rows: [],
            columns: [
                {
                    field: 'timestamp',
                    headerName: 'Time',
                    width: 200,
                    valueFormatter: formatChartTimestamp,
                    renderHeader: () => _jsx(_Fragment, { children: "Time" }),
                },
            ],
        };
        // Assemble rows
        const mockAbh = new AutoBucketHistory({ timeBucketDef, defaultBucketStrategy: BucketStrategy.APPORTION });
        mockAbh.push({ timestamp: toDateValue.valueOf(), state: { mock: 10 } }, true, fromDateValue.valueOf());
        console.log(mockAbh);
        const mockSnapshots = mockAbh.getStateSnapshotArray();
        mockAbh.getStateSnapshotArray().forEach((snap) => {
            if (snap.timestamp == toDateValue.valueOf())
                return;
            data.rows.push({
                id: `${snap.timestamp}`,
                type: 'ComparisonTableRow',
                timestamp: snap.timestamp,
            });
        });
        console.log('Rows before data insert', data.rows);
        // Put Data into Rows
        comparisonEntries.forEach((entry) => {
            ;
            ['readings', 'observations'].forEach((readingType) => {
                const bucketed = readingType == 'readings' ? bucketedReadings.current : bucketedObservations.current;
                if (!bucketed[entry.id])
                    return;
                console.log(`Adding Bucketed ${readingType}s for ${entry.id}`);
                const keyPrefix = `${entry.id}_${readingType}`;
                const entryData = bucketed[entry.id];
                // Add column for each key
                entry.keys.forEach((metricKey) => {
                    data.columns.push({
                        field: `${keyPrefix}_${metricKey}`,
                        headerName: makePresentable(metricKey),
                        // For chart
                        fullHeader: `${entry.label} | ${makePresentable(readingType)} | ${makePresentable(metricKey)}`,
                        bucketStrategy: bucketStrategyMaps[entry.entity.type][metricKey],
                    });
                    // Add averaging immediately next if present
                    if (entry.averageKeys.find((ak) => ak == metricKey)) {
                        data.columns.push({
                            field: `${keyPrefix}_${metricKey}_avg`,
                            headerName: `${makePresentable(metricKey)} Avg.`,
                            fullHeader: `${entry.label} | ${makePresentable(readingType)} | ${makePresentable(metricKey)} (Average)`,
                            average: true,
                        });
                    }
                });
                // Add remaining average keys if not already added
                entry.averageKeys.forEach((aKey) => {
                    if (!entry.keys.find((k) => k == aKey)) {
                        data.columns.push({
                            field: `${keyPrefix}_${aKey}_avg`,
                            headerName: `${makePresentable(aKey)} Avg.`,
                            fullHeader: `${entry.label} | ${makePresentable(readingType)} | ${makePresentable(aKey)} (Average)`,
                            average: true,
                        });
                    }
                });
                // Add to rows
                const entryReadingSnapshots = entryData.getStateSnapshotArray();
                entryReadingSnapshots.forEach((snap, idx) => {
                    // Find row based on number of bucket width offsets from end
                    console.log('Processing rows for', entry);
                    console.log('Entry starts at', entry.toDateMs == 'present' ? 'present' : getNiceDate(entry.toDateMs));
                    const bucketFromMs = entry.toDateMs === 'present' ? fromDateValue.valueOf() : entry.toDateMs - (toDateValue.valueOf() - fromDateValue.valueOf());
                    debug && console.log('Bucket starts at', bucketFromMs, getNiceDate(bucketFromMs));
                    const wholeBucketWidthsFromStart = Math.ceil((snap.timestamp - bucketFromMs) / bucketWidthMs); // Bucket math makes this work even if the start timestamp isn't right on a bucket boundary
                    debug && console.log('Bucket Widths:', wholeBucketWidthsFromStart, snap);
                    const row = data.rows[wholeBucketWidthsFromStart];
                    entry.keys.forEach((metricKey) => {
                        row[`${keyPrefix}_${metricKey}`] = snap.state[metricKey];
                    });
                    // Averaging
                    entry.averageKeys.forEach((avgKey) => {
                        let total = 0;
                        let numReadings = 0;
                        for (let idxOffset = -averagingWindow; idxOffset < averagingWindow; idxOffset++) {
                            const avgIdx = idx + idxOffset;
                            if (avgIdx < 0 || avgIdx > entryReadingSnapshots.length - 1)
                                continue;
                            const avgSnap = entryReadingSnapshots[avgIdx];
                            if (![NaN, null, undefined].includes(avgSnap.state[avgKey])) {
                                total += avgSnap.state[avgKey];
                                numReadings += 1;
                            }
                        }
                        row[`${keyPrefix}_${avgKey}_avg`] = numReadings == 0 ? null : total / numReadings;
                    });
                });
            });
        });
        console.log('Table Data', data);
        return data;
    }, [forceDataUpdate, averagingWindow]);
    const columnGroupModel = useMemo(() => {
        const model = [];
        comparisonEntries.forEach((entry) => {
            const entryColGroup = {
                groupId: entry.id,
                renderHeaderGroup: () => _jsx("b", { children: entry.label }),
                children: [], // Readings and Observations, populated below
            };
            ['readings', 'observations'].forEach((readingType) => {
                const bucketed = readingType == 'readings' ? bucketedReadings.current : bucketedObservations.current;
                if (!bucketed[entry.id])
                    return;
                const readingsGroup = {
                    groupId: makePresentable(readingType),
                    children: [], // Every reading key, populated below
                };
                entry.keys.forEach((metricKey) => {
                    readingsGroup.children.push({
                        field: `${entry.id}_${readingType}_${metricKey}`,
                    });
                    if (entry.averageKeys.find((ak) => ak == metricKey)) {
                        readingsGroup.children.push({
                            field: `${entry.id}_${readingType}_${metricKey}_avg`,
                        });
                    }
                });
                entryColGroup.children.push(readingsGroup);
            });
            model.push(entryColGroup);
        });
        console.log('Column group model', model);
        return model;
    }, [tableData]);
    const toExportObjects = (rows) => {
        const exportObjs = rows.map((row, fallbackId) => {
            const exportObj = {
                id: timestampToGoogleSheetsDate(row.timestamp),
                type: '',
                bucket: formatBucketedTimestamp(row.timestamp, timeBucketDef.unit * timeBucketDef.width, fromDateValue.valueOf(), toDateValue.valueOf()),
            };
            const wholeWidths = Math.ceil((row.timestamp - fromDateValue.valueOf()) / bucketWidthMs);
            exportObj.relative = `'+${msToTime(bucketWidthMs * Math.abs(wholeWidths + 1))}`;
            Object.entries(row).forEach(([key, value]) => {
                if (['timestamp', 'type', 'id'].includes(key))
                    return;
                const entryId = key.split('_')[0];
                const entry = comparisonEntries.find((e) => e.id == entryId);
                const keyName = key.split('_').reduce((fullName, curPart, idx) => fullName + (idx < 2 ? '' : (idx == 2 ? '' : '_') + curPart), '');
                let exportObjKey = `${entry.label} | ${makePresentable(key.split('_')[1])} | ${makePresentable(keyName)}`;
                // Keys can pretty easily clash when you can add the same entity twice, so in case that happens we just append the entry id (guaranteed to be unique)
                if (exportObj[exportObjKey])
                    exportObjKey = `${entry.label} (${entry.id}) | ${makePresentable(key.split('_')[1])} | ${makePresentable(keyName)}`;
                exportObj[exportObjKey] = value;
            });
            return exportObj;
        });
        console.log(rows);
        return exportObjs;
    };
    /** UI / UX */
    const dataGridProps = {
        rowHeight: 128,
        getRowHeight: () => 'auto',
        density: 'compact',
        autoHeight: true,
        getRowClassName: (params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'),
    };
    const [viewType, setViewType] = useState('table');
    const gridHeaderStyle = { padding: 1, fontSize: 13 };
    const gridStyle = { padding: 1 };
    const gridSpacing = { label: 5, readings: 0.75, observations: 0.75, keys: 2.5, average: 2.5, delete: 0.5 };
    return (_jsxs(Stack, { spacing: 1, sx: { minWidth: 1200 }, children: [_jsx(ThemeProvider, { theme: smallerInputTheme, children: _jsx(Stack, { spacing: 1, children: _jsxs(Stack, { sx: { border: 'solid 1px #ebebeb', bordertopleftradius: 5, bordertoprightradius: 5 }, children: [_jsxs(Grid, { container: true, children: [_jsx(Grid, { sx: gridHeaderStyle, size: gridSpacing.label, children: "Label" }), _jsx(Grid, { sx: gridHeaderStyle, size: gridSpacing.readings, children: "Readings" }), _jsx(Grid, { sx: gridHeaderStyle, size: gridSpacing.observations, children: "Observations" }), _jsx(Grid, { sx: gridHeaderStyle, size: gridSpacing.keys, children: "Values" }), _jsx(Grid, { sx: gridHeaderStyle, size: gridSpacing.average, children: "Average" })] }), comparisonEntries.map((entry, idx) => (_jsxs(Grid, { container: true, sx: { backgroundColor: idx % 2 == 0 ? '#ebebeb' : '' }, children: [_jsx(Grid, { sx: gridStyle, size: gridSpacing.label, children: _jsxs(Grid, { container: true, spacing: 0.5, children: [_jsx(Grid, { size: entry.toDateMs == 'present' ? 11 : 3, children: _jsx(TextField, { value: entry.label, onChange: (e) => {
                                                            setComparisonEntries((oldEntries) => {
                                                                const oldEntry = oldEntries.find((oe) => oe.id == entry.id);
                                                                oldEntry.label = e.target.value;
                                                                return [...oldEntries];
                                                            });
                                                        }, style: { width: '100%' } }) }), entry.toDateMs == 'present' ? (_jsx(Grid, { size: 1, children: _jsx(Tooltip, { title: "See readings for a different time", children: _jsx(IconButton, { onClick: () => {
                                                                enableDifferentTime(entry);
                                                            }, children: _jsx(AccessTimeIcon, {}) }) }) })) : (_jsxs(_Fragment, { children: [_jsx(Grid, { size: 4, children: _jsx(Tooltip, { title: `${msToTime(entry.toDateMs - (toDateValue.valueOf() - fromDateValue.valueOf()))} from end. This can be changed using the time selection controls below.`, children: _jsx(TextField, { disabled: true, value: getNiceDate(entry.toDateMs - (toDateValue.valueOf() - fromDateValue.valueOf())), label: "From" }) }) }), _jsx(Grid, { size: 4, children: _jsx(LocalizationProvider, { dateAdapter: AdapterDayjs, adapterLocale: "en-nz", children: _jsx(DateTimePicker, { value: dayjs(entry.toDateMs), onChange: (newDayJs) => {
                                                                        newDayJs && changeEntryDifferentTime(newDayJs.valueOf(), entry);
                                                                    }, label: "To" }) }) }), _jsx(Grid, { size: 1, children: _jsx(Tooltip, { title: "See present", children: _jsx(IconButton, { onClick: () => {
                                                                        disableDifferentTime(entry);
                                                                    }, children: _jsx(HistoryIcon, {}) }) }) })] }))] }) }), _jsx(Grid, { sx: gridStyle, size: gridSpacing.readings, children: fetchingReadings[entry.id] === true ? (_jsx(CircularProgress, {})) : (_jsx(Checkbox, { checked: !!bucketedReadings.current[entry.id], color: bucketedReadings.current[entry.id] && bucketedReadings.current[entry.id].getLength() > 0 ? 'primary' : 'error', onClick: () => {
                                                toggleReadings(entry);
                                            } })) }), _jsx(Grid, { sx: gridStyle, size: gridSpacing.observations, children: fetchingObservations[entry.id] === true ? (_jsx(CircularProgress, {})) : (_jsx(Checkbox, { checked: !!bucketedObservations.current[entry.id], color: bucketedObservations.current[entry.id] && bucketedObservations.current[entry.id].getLength() > 0 ? 'primary' : 'error', onClick: () => {
                                                toggleObservations(entry);
                                            } })) }), _jsx(Grid, { sx: gridStyle, size: gridSpacing.keys, children: _jsx(Autocomplete, { multiple: true, options: entry.allowedKeys, value: entry.keys, onChange: (e, value, reason, detail) => {
                                                updateKeys(reason, detail, entry);
                                            }, renderInput: (props) => _jsx(TextField, { ...props }) }) }), _jsx(Grid, { sx: gridStyle, size: gridSpacing.average, children: _jsx(Autocomplete, { multiple: true, options: entry.allowedKeys, value: entry.averageKeys, onChange: (e, value, reason, detail) => {
                                                updateAverageKeys(reason, detail, entry);
                                            }, renderInput: (props) => _jsx(TextField, { ...props }) }) }), _jsx(Grid, { sx: gridStyle, size: gridSpacing.delete, children: _jsx(IconButton, { onClick: () => {
                                                removeComparisonEntry(entry);
                                            }, children: _jsx(ActionIcons.Delete, {}) }) })] }, entry.id))), _jsxs(Grid, { container: true, sx: { borderTop: 'solid 1px #ebebeb', paddingTop: 1 }, children: [_jsx(Grid, { sx: gridStyle, size: gridSpacing.label, children: _jsxs(Stack, { direction: "row", spacing: 1, children: [entityTypeSelector, entitySelector] }) }), _jsx(Grid, { size: 3, children: _jsx(IconButton, { onClick: addComparisonEntry, children: _jsx(AddCircleIcon, {}) }) })] })] }) }) }), _jsx(Stack, { direction: "row", spacing: 1, children: _jsxs(ThemeProvider, { theme: smallerInputTheme, children: [timeSelectorComponents.periodOrRecentRadio, timeSelectorDurationMode == PeriodRecent.PERIOD && (_jsxs(_Fragment, { children: [timeSelectorComponents.shiftEarlierBtn, timeSelectorComponents.fromPicker, timeSelectorComponents.toPicker, timeSelectorComponents.shiftLaterBtn, timeSelectorComponents.resetToNowBtn, timeSelectorComponents.confirmButton] })), timeSelectorDurationMode == PeriodRecent.RECENT && (_jsxs(_Fragment, { children: [timeSelectorComponents.periodField, timeSelectorComponents.rangeUnitRadio, timeSelectorComponents.confirmButton] })), bucketComponents.unit, bucketComponents.width, _jsx(Tooltip, { title: `The number of readings to use either side to calculate a moving average, currently ${decimalMinutesToTimeString(bucketWidthMs / (Milliseconds.MINUTE * averagingWindow))} either side`, children: _jsx(TextField, { slotProps: { htmlInput: { min: 1 } }, type: "number", label: "Avg Window", value: averagingWindow, onChange: (e) => {
                                    setAveragingWindow(parseInt(e.target.value));
                                } }) })] }) }), _jsxs(Stack, { direction: "row", spacing: 1, children: [_jsx(Tooltip, { title: 'View Table', children: _jsx(ToggleButton, { value: 'table', selected: viewType === 'table', onChange: () => setViewType('table'), color: 'primary', children: _jsx(TableViewIcon, {}) }, 'table') }), _jsx(Tooltip, { title: 'View Chart', children: _jsx(ToggleButton, { value: 'graph', selected: viewType === 'graph', onChange: () => setViewType('graph'), color: 'primary', children: _jsx(SsidChartIcon, {}) }, 'graph') }), tableData && (_jsx(SimpleIOModal, { initialIOType: IOType.EXPORT, allowIOTypeToggle: false, plainObjects: toExportObjects(tableData.rows), setPlainObjectsInParent: () => { } })), _jsx(Tooltip, { title: "Display time relative to start date", children: _jsx(ToggleButton, { value: "Relative Time", color: "primary", selected: timeDisplay === 'bucketWidthsSinceStart', onClick: () => {
                                setTimeDisplay((old) => (old == 'concreteBuckets' ? 'bucketWidthsSinceStart' : 'concreteBuckets'));
                                setForceDataUpdate((u) => !u);
                            }, children: _jsx(AvTimerIcon, {}) }) })] }), viewType === 'table' && (_jsx(Box, { sx: { width: '100%' }, height: 600, children: tableData && _jsx(StripedDataGrid, { columns: tableData.columns, rows: tableData.rows, columnGroupingModel: columnGroupModel, ...dataGridProps }) })), viewType === 'graph' && (_jsx(Box, { sx: { width: '100%' }, height: 600, children: tableData && (_jsx(ComparisonChart, { data: tableData, timeParams: { bucketWidthMs: timeBucketDef.unit * timeBucketDef.width, fromDateValue, toDateValue }, averageWindow: averagingWindow })) }))] }));
};
